import { useEffect, useCallback, useRef } from 'react';
// react-router-dom v6.16 ships useBlocker under the unstable_ prefix in this build
import { unstable_useBlocker as useBlocker } from 'react-router-dom';

/**
 * Intercepts React Router navigation and browser refresh/close when the form
 * has unsaved changes. Call `allowNavigation()` before any programmatic
 * navigate() call that happens after a successful save so the blocker does not
 * fire for that intentional navigation.
 */
export function useUnsavedChangesWarning(isDirty: boolean) {
  // Ref mirrors the latest isDirty so the blocker closure is never stale.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  // Set to true just before programmatic navigation after a successful save so
  // the blocker does not intercept it.
  const allowRef = useRef(false);

  const blocker = useBlocker(
    ({
      currentLocation,
      nextLocation,
    }: {
      currentLocation: { pathname: string };
      nextLocation: { pathname: string };
    }) => {
      if (allowRef.current) return false;
      return (
        isDirtyRef.current &&
        currentLocation.pathname !== nextLocation.pathname
      );
    }
  );

  // Browser refresh / tab-close protection.
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Call before programmatic navigate() that follows a successful save.
  const allowNavigation = useCallback(() => {
    allowRef.current = true;
  }, []);

  // User clicked "Leave Without Saving" in the modal.
  const confirmNavigation = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed?.();
    }
  }, [blocker]);

  // User clicked "Stay on Page" in the modal.
  const cancelNavigation = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset?.();
    }
  }, [blocker]);

  return {
    showModal: blocker.state === 'blocked',
    confirmNavigation,
    cancelNavigation,
    allowNavigation,
  };
}
